import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import SendIcon from "@material-ui/icons/Send";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

const CATALOG_EXPANDED_KEY = "contactDrawer:catalogSheetsExpanded";
const NO_CATALOG_VARIATION = "__no_catalog_variation__";
const imageCache = new Map();

const useStyles = makeStyles(theme => ({
	panel: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},
	row: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "center",
	},
	currencyField: {
		width: 82,
		flexShrink: 0,
	},
	status: {
		color: theme.palette.text.secondary,
		fontSize: 12,
	},
	statusSuccess: {
		color: theme.palette.success.main,
	},
	statusError: {
		color: theme.palette.error.main,
	},
	empty: {
		padding: theme.spacing(2),
		color: theme.palette.text.secondary,
		textAlign: "center",
	},
	searchWrapper: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1),
	},
	searchIcon: {
		color: theme.palette.text.secondary,
	},
	card: {
		height: "100%",
		padding: theme.spacing(1),
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.75),
	},
	imageBox: {
		height: 250,
		background: "#fff",
		border: "1px solid rgba(0, 0, 0, 0.08)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		overflow: "hidden",
	},
	image: {
		width: "100%",
		height: "100%",
		objectFit: "contain",
	},
	productName: {
		fontWeight: 600,
		wordBreak: "break-word",
	},
	price: {
		color: theme.palette.success.main,
		fontWeight: 600,
	},
	oldPrice: {
		textDecoration: "line-through",
		color: theme.palette.text.secondary,
		marginRight: theme.spacing(0.5),
	},
	attributes: {
		padding: theme.spacing(0.75),
		background: theme.palette.action.hover,
	},
	variations: {
		padding: theme.spacing(0.75),
		background: theme.palette.action.hover,
	},
	variationControl: {
		marginTop: theme.spacing(0.5),
	},
	attributeLine: {
		display: "flex",
		gap: theme.spacing(0.5),
		wordBreak: "break-word",
	},
	progress: {
		marginRight: theme.spacing(1),
	},
	chips: {
		display: "flex",
		gap: theme.spacing(0.5),
		flexWrap: "wrap",
	},
}));

const normalizeImageUrl = value => {
	if (!value) return "";
	const url = String(value);
	if (!url.includes("drive.google.com")) return url;

	const pathFileId = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
	if (pathFileId) {
		return `https://drive.google.com/uc?export=view&id=${pathFileId[1]}`;
	}

	try {
		const parsedUrl = new URL(url);
		const queryFileId = parsedUrl.searchParams.get("id");
		if (queryFileId) {
			return `https://drive.google.com/uc?export=view&id=${queryFileId}`;
		}
	} catch (err) {
		return url;
	}

	return url;
};

const getVariationOptions = product => {
	const options = {};
	(product.variations || []).forEach(variation => {
		Object.entries(variation.attributes || {}).forEach(([attribute, value]) => {
			if (!value) return;
			if (!options[attribute]) options[attribute] = new Set();
			options[attribute].add(value);
		});
	});

	return Object.entries(options).reduce((acc, [attribute, values]) => {
		acc[attribute] = Array.from(values);
		return acc;
	}, {});
};

const findMatchingVariation = (product, selectedAttributes = {}) => {
	const requiredAttributes = Object.keys(getVariationOptions(product));
	if (!requiredAttributes.length) return null;

	const hasAllSelections = requiredAttributes.every(attribute => selectedAttributes[attribute]);
	if (!hasAllSelections) return null;

	return (product.variations || []).find(variation =>
		requiredAttributes.every(
			attribute => variation.attributes?.[attribute] === selectedAttributes[attribute]
		)
	);
};

const CatalogImage = ({ src, alt, classes }) => {
	const [imageSrc, setImageSrc] = useState("");
	const [failed, setFailed] = useState(false);
	const normalizedSrc = useMemo(() => normalizeImageUrl(src), [src]);

	useEffect(() => {
		let active = true;

		setFailed(false);
		setImageSrc("");

		if (!normalizedSrc) return undefined;

		if (normalizedSrc.startsWith("data:")) {
			setImageSrc(normalizedSrc);
			return undefined;
		}

		const cachedImage = imageCache.get(normalizedSrc);
		if (cachedImage) {
			setImageSrc(cachedImage);
			return undefined;
		}

		api
			.get("/catalog-sheets/image", {
				params: { url: normalizedSrc },
				responseType: "blob",
			})
			.then(({ data }) => {
				if (!active) return;
				const objectUrl = URL.createObjectURL(data);
				imageCache.set(normalizedSrc, objectUrl);
				setImageSrc(objectUrl);
			})
			.catch(() => {
				if (active) setFailed(true);
			});

		return () => {
			active = false;
		};
	}, [normalizedSrc]);

	if (!normalizedSrc || failed) {
		return <Typography color="textSecondary">Sin imagen</Typography>;
	}

	if (!imageSrc) {
		return <CircularProgress size={18} />;
	}

	return (
		<img
			className={classes.image}
			src={imageSrc}
			alt={alt}
			onError={() => setFailed(true)}
		/>
	);
};

const CatalogSheetsPanel = () => {
	const classes = useStyles();
	const { ticketId } = useParams();
	const [expanded, setExpanded] = useState(() => {
		const saved = localStorage.getItem(CATALOG_EXPANDED_KEY);
		return saved === null ? true : saved === "true";
	});
	const [spreadsheetId, setSpreadsheetId] = useState("");
	const [sheetName, setSheetName] = useState("");
	const [currencySymbol, setCurrencySymbol] = useState("$");
	const [products, setProducts] = useState([]);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [sendingProductId, setSendingProductId] = useState("");
	const [selectedVariations, setSelectedVariations] = useState({});
	const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
	const [status, setStatus] = useState({
		text: "Configura un Google Sheet para ver el catalogo.",
		type: "info",
	});

	const updateStatus = (text, type = "info") => setStatus({ text, type });

	const handleToggleExpanded = () => {
		setExpanded(prev => {
			const next = !prev;
			localStorage.setItem(CATALOG_EXPANDED_KEY, String(next));
			return next;
		});
	};

	const formatCurrency = value => `${currencySymbol || "$"}${value}`;

	const formatPrice = product => {
		if (product.type === "variable" && product.minPrice !== undefined) {
			if (product.minSalePrice && product.maxSalePrice && product.minRegularPrice) {
				const regularRange =
					product.minRegularPrice === product.maxRegularPrice
						? formatCurrency(product.minRegularPrice)
						: `${formatCurrency(product.minRegularPrice)} - ${formatCurrency(product.maxRegularPrice)}`;
				const saleRange =
					product.minSalePrice === product.maxSalePrice
						? formatCurrency(product.minSalePrice)
						: `${formatCurrency(product.minSalePrice)} - ${formatCurrency(product.maxSalePrice)}`;

				return (
					<>
						<span className={classes.oldPrice}>{regularRange}</span>
						<br />
						{saleRange}
					</>
				);
			}

			return product.minPrice === product.maxPrice
				? formatCurrency(product.minPrice)
				: `${formatCurrency(product.minPrice)} - ${formatCurrency(product.maxPrice)}`;
		}

		if (product.salePrice && product.price && product.salePrice < product.price) {
			return (
				<>
					<span className={classes.oldPrice}>{formatCurrency(product.price)}</span>
					{formatCurrency(product.salePrice)}
				</>
			);
		}

		const price = product.salePrice || product.price || "";
		return price ? formatCurrency(price) : "Sin precio";
	};

	const getProductPriceText = product => {
		if (product.type === "variable" && product.minPrice !== undefined) {
			if (product.minSalePrice && product.maxSalePrice && product.minRegularPrice) {
				const regularRange =
					product.minRegularPrice === product.maxRegularPrice
						? formatCurrency(product.minRegularPrice)
						: `${formatCurrency(product.minRegularPrice)} - ${formatCurrency(product.maxRegularPrice)}`;
				const saleRange =
					product.minSalePrice === product.maxSalePrice
						? formatCurrency(product.minSalePrice)
						: `${formatCurrency(product.minSalePrice)} - ${formatCurrency(product.maxSalePrice)}`;
				return `Antes: ${regularRange}\nAhora: ${saleRange}`;
			}

			return product.minPrice === product.maxPrice
				? formatCurrency(product.minPrice)
				: `${formatCurrency(product.minPrice)} - ${formatCurrency(product.maxPrice)}`;
		}

		return formatItemPrice(product);
	};

	const formatItemPrice = item => {
		if (!item) return "";
		if (item.salePrice && item.price && item.salePrice < item.price) {
			return `${formatCurrency(item.salePrice)} (antes ${formatCurrency(item.price)})`;
		}
		const price = item.salePrice || item.price || "";
		return price ? formatCurrency(price) : "";
	};

	const buildProductCaption = (product, selectedVariation = null) => {
		const item = selectedVariation || product;
		const lines = [`🛍️ *${product.name || "Producto"}*`];

		if (product.sku || item.sku) lines.push(`SKU: ${item.sku || product.sku}`);
		if (product.category) lines.push(`🏷️ *Categoria:* ${product.category}`);
		if (product.description) lines.push("", `📝 *Descripcion:* ${product.description}`);

		const attributes = selectedVariation?.attributes || product.attributes || {};
		const attributeLines = Object.entries(attributes)
			.filter(([, value]) => value)
			.map(([key, value]) => `• *${key}:* ${value}`);
		if (attributeLines.length) lines.push("", "🎯 *Opciones:*", ...attributeLines);

		const price = selectedVariation
			? formatItemPrice(selectedVariation)
			: getProductPriceText(product);
		if (price) lines.push("", `💰 *Precio:* ${price}`);
		lines.push("", "💬 _Responde para consultar disponibilidad_");

		return lines.filter(line => line !== undefined && line !== null).join("\n");
	};

	const handleVariationChange = (productId, attribute, value) => {
		setSelectedVariations(prev => ({
			...prev,
			[productId]: {
				...(prev[productId] || {}),
				[attribute]: value,
			},
		}));
	};

	const handleSendProduct = async product => {
		if (!ticketId) {
			updateStatus("No hay ticket activo para enviar la ficha.", "error");
			return;
		}

		const selectedAttributes = selectedVariations[product.id] || {};
		const variationOptions = getVariationOptions(product);
		const hasVariationSelectors = Object.keys(variationOptions).length > 0;
		const selectedVariation = findMatchingVariation(product, selectedAttributes);
		const hasAllSelections = Object.keys(variationOptions).every(
			attribute => selectedAttributes[attribute]
		);

		if (hasVariationSelectors && hasAllSelections && !selectedVariation) {
			updateStatus("La combinacion seleccionada no esta disponible.", "error");
			return;
		}

		setSendingProductId(product.id);
		try {
			await api.post(`/catalog-sheets/products/send/${ticketId}`, {
				productId: product.id,
				selectedAttributes: selectedVariation ? selectedAttributes : {},
			});

			toast.success("Ficha del producto enviada.");
			updateStatus(`Ficha enviada: ${product.name}`, "success");
		} catch (err) {
			updateStatus("No se pudo enviar la ficha del producto.", "error");
			toastError(err);
		} finally {
			setSendingProductId("");
		}
	};

	const filteredProducts = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return products;

		return products.filter(product => {
			const values = [
				product.name,
				product.description,
				product.sku,
				product.category,
				product.id,
				...Object.values(product.attributes || {}),
				...(product.variations || []).flatMap(variation => [
					variation.id,
					variation.sku,
					...Object.values(variation.attributes || {}),
				]),
			];
			return values.some(value => String(value || "").toLowerCase().includes(term));
		});
	}, [products, search]);

	const loadConfig = async () => {
		try {
			const { data } = await api.get("/catalog-sheets/config");
			const config = data.config;
			if (!config) return;

			setSpreadsheetId(config.spreadsheetId || "");
			setSheetName(config.sheetName || "");
			setCurrencySymbol(config.currencySymbol || "$");
			await loadProducts();
		} catch (err) {
			toastError(err);
		}
	};

	const loadProducts = async () => {
		setLoading(true);
		updateStatus("Cargando productos...", "info");
		try {
			const { data } = await api.get("/catalog-sheets/products");
			setProducts(data.products || []);
			updateStatus(`${data.products?.length || 0} producto(s) cargados.`, "success");
		} catch (err) {
			setProducts([]);
			updateStatus("No se pudieron cargar los productos del catalogo.", "error");
			if (err?.response?.status !== 404) toastError(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadConfig();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSave = async () => {
		const trimmedSpreadsheetId = spreadsheetId.trim();
		const trimmedSheetName = sheetName.trim();

		if (!trimmedSpreadsheetId || !trimmedSheetName) {
			updateStatus("Completa el ID del documento y la hoja.", "error");
			return;
		}

		try {
			await api.put("/catalog-sheets/config", {
				spreadsheetId: trimmedSpreadsheetId,
				sheetName: trimmedSheetName,
				currencySymbol: currencySymbol.trim() || "$",
			});
			toast.success("Catálogo guardado.");
			await loadProducts();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClear = async () => {
		try {
			await api.delete("/catalog-sheets/config");
			setSpreadsheetId("");
			setSheetName("");
			setCurrencySymbol("$");
			setProducts([]);
			setSearch("");
			updateStatus("Configuracion del catalogo eliminada.", "success");
		} catch (err) {
			toastError(err);
		}
	};

	const statusClass =
		status.type === "success"
			? classes.statusSuccess
			: status.type === "error"
			? classes.statusError
			: "";

	return (
		<Paper square variant="outlined" className={classes.panel}>
			<div className={classes.header}>
				<Typography variant="subtitle1">Catálogo Google Sheets</Typography>
				<div>
					<IconButton
						size="small"
						disabled={loading || !products.length}
						onClick={loadProducts}
						title="Actualizar catalogo"
					>
						<RefreshIcon fontSize="small" />
					</IconButton>
					<IconButton size="small" onClick={handleToggleExpanded}>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</div>
			</div>

			<Collapse in={expanded}>
				<div className={classes.row}>
					<TextField
						label="ID del Google Sheet"
						value={spreadsheetId}
						onChange={event => setSpreadsheetId(event.target.value)}
						variant="outlined"
						margin="dense"
						size="small"
						fullWidth
					/>
					<TextField
						label="Hoja"
						value={sheetName}
						onChange={event => setSheetName(event.target.value)}
						variant="outlined"
						margin="dense"
						size="small"
						style={{ width: 90 }}
					/>
				</div>
				<div className={classes.row}>
					<TextField
						label="Moneda"
						value={currencySymbol}
						onChange={event => setCurrencySymbol(event.target.value.slice(0, 8))}
						variant="outlined"
						margin="dense"
						size="small"
						className={classes.currencyField}
					/>
					<Button variant="contained" color="primary" size="small" onClick={handleSave} disabled={loading}>
						Guardar
					</Button>
					<IconButton
						size="small"
						onClick={() => setClearConfirmOpen(true)}
						title="Limpiar configuracion"
					>
						<DeleteOutlineIcon fontSize="small" />
					</IconButton>
				</div>

				<Typography className={`${classes.status} ${statusClass}`}>
					{loading && <CircularProgress size={12} className={classes.progress} />}
					{status.text}
				</Typography>

				{products.length ? (
					<>
						<Paper square variant="outlined" className={classes.searchWrapper}>
							<SearchIcon className={classes.searchIcon} fontSize="small" />
							<TextField
								value={search}
								onChange={event => setSearch(event.target.value)}
								placeholder="Buscar por nombre, descripcion, SKU o atributo"
								margin="dense"
								size="small"
								fullWidth
							/>
							<Button size="small" onClick={() => setSearch("")}>
								Limpiar
							</Button>
						</Paper>
						<Typography variant="caption" color="textSecondary">
							{filteredProducts.length} de {products.length} producto(s)
						</Typography>
						<Grid container spacing={1}>
							{filteredProducts.map(product => (
								<Grid item xs={12} key={product.id}>
									{(() => {
										const variationOptions = getVariationOptions(product);
										const selectedAttributes = selectedVariations[product.id] || {};
										const selectedVariation = findMatchingVariation(product, selectedAttributes);
										const hasVariationSelectors = Object.keys(variationOptions).length > 0;
										const hasAllSelections = Object.keys(variationOptions).every(
											attribute => selectedAttributes[attribute]
										);
										const hasInvalidVariation =
											hasVariationSelectors && hasAllSelections && !selectedVariation;
										const displayPrice = selectedVariation
											? formatPrice(selectedVariation)
											: hasInvalidVariation
											? "No disponible"
											: formatPrice(product);
										const sendLabel =
											product.type === "variable" && !selectedVariation
												? "Enviar ficha completa"
												: "Enviar ficha";

										return (
									<Paper square variant="outlined" className={classes.card}>
										<div className={classes.imageBox}>
											<CatalogImage
												src={
													selectedVariation?.image || product.image
												}
												alt={product.name}
												classes={classes}
											/>
										</div>
										<Typography variant="body2" className={classes.productName}>
											{product.name}
										</Typography>
										<div className={classes.chips}>
											{product.sku && <Chip size="small" label={`SKU ${product.sku}`} />}
											{product.category && <Chip size="small" label={product.category} />}
											<Chip size="small" label={product.type === "variable" ? "Variable" : "Simple"} />
										</div>
										<Typography variant="body2" className={classes.price}>
											{displayPrice}
										</Typography>
										{product.description && (
											<Typography variant="body2" color="textSecondary">
												{product.description}
											</Typography>
										)}
										{Object.keys(product.attributes || {}).length > 0 && (
											<Paper square variant="outlined" className={classes.attributes}>
												{Object.entries(product.attributes).map(([key, value]) => (
													<Typography key={key} variant="caption" component="div" className={classes.attributeLine}>
														<strong>{key}:</strong> {value}
													</Typography>
												))}
											</Paper>
										)}
										{product.variations?.length > 0 && (
											<Paper square variant="outlined" className={classes.variations}>
												<Typography variant="caption" color="textSecondary">
													Opciones disponibles
												</Typography>
												{Object.entries(variationOptions).map(([attribute, values]) => (
													<FormControl
														key={attribute}
														variant="outlined"
														margin="dense"
														size="small"
														fullWidth
														className={classes.variationControl}
													>
														<InputLabel>{attribute}</InputLabel>
														<Select
															value={
																selectedVariations[product.id]?.[attribute] ||
																NO_CATALOG_VARIATION
															}
															onChange={event =>
																handleVariationChange(
																	product.id,
																	attribute,
																	event.target.value === NO_CATALOG_VARIATION
																		? ""
																		: event.target.value
																)
															}
															label={attribute}
														>
															<MenuItem value={NO_CATALOG_VARIATION}>Seleccionar {attribute}</MenuItem>
															{values.map(value => (
																<MenuItem key={value} value={value}>
																	{value}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												))}
											</Paper>
										)}
										<Button
											variant="contained"
											color="primary"
											size="small"
											startIcon={
												sendingProductId === product.id ? (
													<CircularProgress size={14} color="inherit" />
												) : (
													<SendIcon fontSize="small" />
												)
											}
											disabled={Boolean(sendingProductId) || hasInvalidVariation}
											onClick={() => handleSendProduct(product)}
										>
											{hasInvalidVariation ? "No disponible" : sendLabel}
										</Button>
									</Paper>
										);
									})()}
								</Grid>
							))}
						</Grid>
					</>
				) : (
					<Typography className={classes.empty} variant="body2">
						Configura tu Google Sheet para ver el catalogo de productos.
					</Typography>
				)}
			</Collapse>
			<ConfirmationModal
				title="Limpiar configuracion"
				open={clearConfirmOpen}
				onClose={() => setClearConfirmOpen(false)}
				onConfirm={handleClear}
			>
				¿Seguro que deseas limpiar la configuracion del catalogo?
			</ConfirmationModal>
		</Paper>
	);
};

export default CatalogSheetsPanel;

